import React, { useState } from "react";
import { Heading } from "../../../design/heading";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../../design/description-list";
import { Button } from "../../../design/button";
import { Participant } from "../../../models/Participant";
import EmploymentForm from "../../../components/Forms/EmploymentForm";
import moment from "moment";
import EmptyStateView from "../../../components/EmptyStateView";

interface ParticipantEmploymentProps {
  participant: Participant;
}

const ParticipantEmployment: React.FC<ParticipantEmploymentProps> = ({
  participant,
}) => {
  const [showEmploymentForm, setShowEmploymentForm] = useState(false);

  const shouldShowEmptyStateView =
    !participant.employment?.employer &&
    !participant.employment?.occupation &&
    !participant.employment?.phone &&
    !participant.employment?.address &&
    !participant.employment?.startDate &&
    !participant.employment?.endDate &&
    participant.employment?.hoursPerWeek === undefined &&
    participant.employment?.grossPay === undefined;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between">
        <Heading>Employment</Heading>
        <Button
          plain
          type="button"
          onClick={() => setShowEmploymentForm((prev) => !prev)}
        >
          {showEmploymentForm ? "Hide" : "Edit Employment"}
        </Button>
      </div>

      {shouldShowEmptyStateView && !showEmploymentForm && (
        <div className="mt-4">
          <EmptyStateView
            title="No Employment Details"
            message="No employment details found for this participant."
          />
        </div>
      )}

      {/* Employment Details */}
      {!showEmploymentForm && (
        <div className="mt-4">
          <DescriptionList>
            {participant.employment?.employer && (
              <>
                <DescriptionTerm>Employer</DescriptionTerm>
                <DescriptionDetails>
                  {participant.employment.employer}
                </DescriptionDetails>
              </>
            )}
            {participant.employment?.occupation && (
              <>
                <DescriptionTerm>Occupation</DescriptionTerm>
                <DescriptionDetails>
                  {participant.employment.occupation}
                </DescriptionDetails>
              </>
            )}
            {participant.employment?.phone && (
              <>
                <DescriptionTerm>Phone</DescriptionTerm>
                <DescriptionDetails>
                  {participant.employment.phone}
                </DescriptionDetails>
              </>
            )}
            {participant.employment?.address && (
              <>
                <DescriptionTerm>Address</DescriptionTerm>
                <DescriptionDetails>
                  {participant.employment.address}
                </DescriptionDetails>
              </>
            )}
            {participant.employment?.startDate && (
              <>
                <DescriptionTerm>Start Date</DescriptionTerm>
                <DescriptionDetails>
                  {moment(participant.employment.startDate).format(
                    "MM/DD/YYYY",
                  )}
                </DescriptionDetails>
              </>
            )}
            {participant.employment?.endDate && (
              <>
                <DescriptionTerm>End Date</DescriptionTerm>
                <DescriptionDetails>
                  {moment(participant.employment.endDate).format("MM/DD/YYYY")}
                </DescriptionDetails>
              </>
            )}
            {participant.employment?.hoursPerWeek !== undefined && (
              <>
                <DescriptionTerm>Hours Per Week</DescriptionTerm>
                <DescriptionDetails>
                  {participant.employment.hoursPerWeek}
                </DescriptionDetails>
              </>
            )}
            {participant.employment?.grossPay !== undefined && (
              <>
                <DescriptionTerm>Gross Pay</DescriptionTerm>
                <DescriptionDetails>
                  {participant.employment.grossPay}
                  {participant.employment.grossPayFrequency &&
                    ` / ${participant.employment.grossPayFrequency}`}
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>
        </div>
      )}

      {/* Employment Form */}
      {showEmploymentForm && (
        <div className="mt-6">
          <EmploymentForm
            participantId={participant._id}
            value={participant.employment}
            onFormSubmitted={() => setShowEmploymentForm(false)}
          />
        </div>
      )}
    </div>
  );
};

export default ParticipantEmployment;
